import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';

const UserQuestions = () => {
    const { user } = useAuth();
    const { config } = useConfig();
    const [questions, setQuestions] = useState([]);
    const [newQuestion, setNewQuestion] = useState({ text: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');

    const textGradientStyle = {
        background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent'
    };

    const buttonGradientStyle = {
        background: 'linear-gradient(to right, #033f83, #079acf, #65bade)'
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 2000);
        return () => clearInterval(interval);
    }, []);

    const fetchData = async () => {
        try {
            const qRes = await api.get('/questions');
            setQuestions(qRes.data);
        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newQuestion.text) return;
        
        setIsSubmitting(true);
        setMessage('');
        try {
            await api.post('/questions', { ...newQuestion, speaker_id: null });
            setMessage('¡Pregunta enviada con éxito!');
            setNewQuestion({ text: '' });
            fetchData();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error submitting question", error);
            setMessage('Error al enviar la pregunta.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-md mx-auto animate-fadeIn pb-24 mt-12 px-4 sm:px-0">
            {/* Form Section matched to Image 3 */}
            <div className="relative mb-20">
                {/* Floating Title */}
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 z-10 w-[90%] sm:w-[85%] flex justify-center">
                    <div className="bg-[#f58220] text-white py-3 px-6 text-center font-black text-lg sm:text-xl rounded-sm shadow-md uppercase tracking-wider w-full">
                        Preguntas al expositor
                    </div>
                </div>
                
                {/* Main Box */}
                <div className="bg-white border-[1.5px] border-[#033f83] rounded-md pt-16 pb-8 px-8 relative shadow-sm min-h-[220px]">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Question Text */}
                        <div>
                            <textarea 
                                className="w-full bg-transparent text-slate-500 font-bold text-lg sm:text-xl outline-none min-h-[120px] resize-none placeholder:text-slate-300"
                                placeholder="Escribe tu pregunta aquí..."
                                value={newQuestion.text}
                                onChange={e => setNewQuestion({...newQuestion, text: e.target.value})}
                                required
                            />
                        </div>

                        {message && (
                            <div className={`text-center text-xs font-bold p-2 ${message.includes('éxito') ? 'text-emerald-500' : 'text-red-500'}`}>
                                {message}
                            </div>
                        )}
                    </form>
                </div>

                {/* Submit Button */}
                <div className="w-[70%] mx-auto mt-6 flex justify-center relative z-20">
                    <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting || !newQuestion.text}
                        className="w-full bg-[#033f83] text-white py-3.5 font-black text-lg sm:text-xl rounded-sm shadow-[0_5px_15px_rgba(3,63,131,0.3)] transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
                    >
                        {isSubmitting ? 'Enviando...' : 'Enviar pregunta'}
                    </button>
                </div>
            </div>

            {/* Salir Button */}
            <div className="flex justify-center mt-12 mb-16">
                <button 
                    onClick={() => window.history.back()}
                    className="w-[70%] bg-[#f58220] text-white py-3.5 font-black text-lg sm:text-xl rounded-sm shadow-[0_5px_15px_rgba(245,130,32,0.3)] transition-all hover:scale-[1.02] active:scale-95"
                >
                    Salir
                </button>
            </div>

            {/* History Section (Kept for functionality, styled minimally) */}
            {questions.length > 0 && (
                <div className="mt-8 pt-8 border-t border-slate-200">
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6 text-center">Mi Historial de Preguntas</h3>
                    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                        {questions.map(q => (
                            <div key={q.id} className="bg-white/50 border border-[#033f83]/20 p-5 rounded-xl">
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-[#033f83]">Pregunta Enviada</span>
                                    <span className="text-[9px] text-slate-400 font-bold">{new Date(q.created_at).toLocaleDateString()}</span>
                                </div>
                                <p className="text-slate-700 font-bold text-sm leading-relaxed mb-3">"{q.text}"</p>
                                
                                {q.reply ? (
                                    <div className="bg-[#033f83]/5 border border-[#033f83]/10 p-3 rounded-lg relative mt-2">
                                        <span className="text-[#f58220] block text-[9px] font-black uppercase mb-1">Respuesta del Staff</span>
                                        <p className="text-[#033f83] text-xs font-bold leading-relaxed">{q.reply}</p>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-400 mt-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-pulse"></div>
                                        Pendiente de respuesta
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserQuestions;
